import { Controller } from "@hotwired/stimulus";

type Stored = {
	key?: string;
	name?: string;
	description?: string;
	creates_mechanic?: string;
	mechanic_config?: Record<string, unknown>;
};
type Field = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

// The options of one "construção variável" ability: rows the author adds, removes and
// reorders, and a small preview of what the Oficina, the sheet and the race page show.
// The server renders the row template and every sentence (phrases value); the card's
// saved config opens the rows once. race-mechanic owns the "Na ficha" line.
export default class extends Controller<HTMLElement> {
	static targets = ["list", "row", "item", "count", "builder", "sheet", "page"];
	static values = { phrases: Object };

	declare readonly listTarget: HTMLElement;
	declare readonly rowTarget: HTMLTemplateElement;
	declare readonly itemTargets: HTMLElement[];
	declare readonly countTarget: HTMLElement;
	declare readonly builderTarget: HTMLElement;
	declare readonly sheetTarget: HTMLElement;
	declare readonly pageTarget: HTMLElement;
	declare readonly phrasesValue: Record<string, string>;

	private picks: string[] = [];

	connect() {
		// Sortable moves the card, which reconnects this: only the first connect may hydrate.
		if (!this.element.dataset.hydrated) {
			const card = this.element.closest<HTMLElement>("[data-mechanic-config]");
			const stored: Stored[] = JSON.parse(card?.dataset.mechanicConfig ?? "{}").available_options ?? [];
			const kept: string[] = JSON.parse(card?.dataset.storedKeys ?? "[]");
			// A key the database does not hold yet (a failed save) is made again from the name.
			for (const option of stored) this.addRow(kept.includes(option.key ?? "") ? option : { ...option, key: "" });
			if (stored.length === 0) this.addRow();
			this.element.dataset.hydrated = "true";
		}
		// race-mechanic reads the rows for its line and disables the fields of a type that is not picked.
		this.changed();
	}

	add() {
		this.addRow();
		this.changed();
	}

	remove(event: Event) {
		(event.currentTarget as HTMLElement).closest("[data-race-construction-target='item']")?.remove();
		this.changed();
	}

	toggleAttack(event: Event) {
		this.showAttack((event.currentTarget as HTMLElement).closest<HTMLElement>("[data-race-construction-target='item']"));
	}

	// Runs on every input in the group: numbers the rows and redraws the three previews.
	render() {
		const rows = this.itemTargets;
		for (const [index, row] of rows.entries()) {
			const number = row.querySelector<HTMLElement>("[data-role='number']");
			if (number) number.textContent = String(index + 1);
		}
		this.countTarget.textContent = this.say(rows.length === 1 ? "count_one" : "count_other", { count: String(rows.length) });

		const named = rows.filter((row) => this.value(row, "name"));
		const typed = Number(this.element.querySelector<HTMLInputElement>("[data-key='selections_required']")?.value);
		const count = Math.min(10, Math.max(1, Math.trunc(typed) || 1));
		this.drawBuilder(named, count);
		this.drawSheet(named, count, Number.isInteger(typed) && typed >= 1 && typed <= 10);
		this.drawPage(named);
	}

	private changed() {
		this.render();
		this.element.dispatchEvent(new Event("input", { bubbles: true }));
	}

	private addRow(option: Stored = {}) {
		const next = Number(this.element.dataset.next ?? 0);
		this.element.dataset.next = String(next + 1);
		const row = this.rowTarget.content.firstElementChild?.cloneNode(true) as HTMLElement;
		row.innerHTML = row.innerHTML.replace(/\[OPTION\]/g, `[${next}]`);
		row.dataset.uid = String(next);
		this.listTarget.appendChild(row);

		const config = option.mechanic_config ?? {};
		const set = (key: string, value: unknown) => {
			const field = row.querySelector<Field>(`[data-key="${key}"]`);
			if (field && value !== undefined && value !== null) field.value = String(value);
		};
		for (const key of ["key", "name", "description"]) set(key, option[key as keyof Stored]);
		const armor = option.creates_mechanic === "base_armor" && config.ac !== undefined;
		const attack = option.creates_mechanic === "natural_weapon" && Object.keys(config).length > 0;
		if (attack) for (const key of ["damage", "damage_type", "weapon_size", "throw_range", "bonus_damage"]) set(key, config[key]);
		if (armor) set("ac", config.ac);

		const check = row.querySelector<HTMLInputElement>("[data-role='attack']");
		if (check) check.checked = attack;
		this.showAttack(row);
		row.querySelector<HTMLElement>("[data-role='attack-toggle']")?.toggleAttribute("hidden", armor);
		const armorBox = row.querySelector<HTMLFieldSetElement>("[data-role='armor']");
		if (armorBox) armorBox.hidden = armorBox.disabled = !armor;
		const note = row.querySelector<HTMLElement>("[data-role='armor-note']");
		if (note && armor) note.textContent = this.say("armor_note", { ac: String(config.ac) });
	}

	private showAttack(row: HTMLElement | null) {
		const weapon = row?.querySelector<HTMLFieldSetElement>("[data-role='weapon']");
		if (!row || !weapon) return;
		weapon.hidden = weapon.disabled = !row.querySelector<HTMLInputElement>("[data-role='attack']")?.checked;
	}

	private value(row: HTMLElement, key: string) {
		return (row.querySelector<Field>(`[data-key="${key}"]`)?.value ?? "").trim();
	}

	private say(key: string, vars: Record<string, string> = {}) {
		return (this.phrasesValue[key] ?? key).replace(/%\{(\w+)\}/g, (_, name) => vars[name] ?? "");
	}

	private heading(text: string) {
		const h = document.createElement("strong");
		h.textContent = text;
		return h;
	}

	private line(text: string, name?: string, error = false) {
		const p = document.createElement("p");
		if (error) p.className = "text-vermelho-700 dark:text-vermelho-300";
		if (name) p.append(this.heading(name), `: ${text}`);
		else p.textContent = text;
		return p;
	}

	// What a player picks in each of the Oficina's dropdowns; the selects send nothing.
	private drawBuilder(rows: HTMLElement[], count: number) {
		const uids = rows.map((row) => row.dataset.uid ?? "");
		this.picks = Array.from({ length: count }, (_, i) => (uids.includes(this.picks[i]) || this.picks[i] === "custom" ? this.picks[i] : ""));
		const nodes: Node[] = [this.heading(this.say("panel_builder"))];
		for (const [index, pick] of this.picks.entries()) {
			const label = document.createElement("label");
			label.className = "flex flex-col gap-1";
			const select = document.createElement("select");
			select.className = "w-full odo-form__select";
			const choices: [string, string][] = [
				["", this.say("placeholder")],
				...rows.map((row): [string, string] => [row.dataset.uid ?? "", this.value(row, "name")]),
				["custom", this.say("custom")],
			];
			for (const [value, text] of choices) select.add(new Option(text, value, false, value === pick));
			select.addEventListener("change", () => {
				this.picks[index] = select.value;
				this.render();
				// The redraw replaced the select the keyboard was on.
				this.builderTarget.querySelectorAll("select")[index]?.focus();
			});
			label.append(this.say("choice", { index: String(index + 1) }), select);
			const hint = document.createElement("p");
			hint.className = "text-ink-muted";
			const row = rows.find((r) => r.dataset.uid === pick);
			hint.textContent = pick === "custom" ? this.say("custom_hint") : row ? this.value(row, "description") : this.say("pick_hint");
			nodes.push(label, hint);
		}
		this.builderTarget.replaceChildren(...nodes);
	}

	private drawSheet(rows: HTMLElement[], count: number, inRange: boolean) {
		const chosen = this.picks.filter(Boolean);
		const nodes: Node[] = [this.heading(this.say("panel_sheet"))];
		// An out-of-range count is the error's to report; the panel shows no count then.
		if (inRange) {
			nodes.push(this.line(this.say("variable_construction", { count: String(count), total: String(this.itemTargets.length) })));
			if (chosen.length < count) nodes.push(this.line(this.say("pending", { count: String(count - chosen.length) })));
		}
		const attacks: [string, boolean][] = [];
		const armors: number[] = [];
		for (const pick of chosen) {
			const row = rows.find((r) => r.dataset.uid === pick);
			if (!row) nodes.push(this.line(this.say("custom_line"), this.say("custom")));
			else {
				nodes.push(this.line(this.value(row, "description"), this.value(row, "name")));
				if (row.querySelector<HTMLInputElement>("[data-role='attack']")?.checked) attacks.push(this.attackLine(row));
				if (row.querySelector<HTMLFieldSetElement>("[data-role='armor']")?.disabled === false) armors.push(Number(this.value(row, "ac")));
			}
		}
		// The sheet takes the heaviest armor picked (Character#race_base_ac).
		if (armors.length) nodes.push(this.line(this.say("base_armor", { value: String(Math.max(...armors)) })));
		if (attacks.length) nodes.push(this.heading(this.say("attack_heading")), ...attacks.map(([text, valid]) => this.line(text, undefined, !valid)));
		this.sheetTarget.replaceChildren(...nodes);
	}

	private attackLine(row: HTMLElement): [string, boolean] {
		const damage = this.value(row, "damage");
		if (!/^\d+d\d+([+-]\d+)?$/.test(damage)) return [this.say("natural_weapon_error"), false];
		const bonus = Number(this.value(row, "bonus_damage"));
		const range = this.value(row, "throw_range");
		const word = (key: string) => (row.querySelector<HTMLSelectElement>(`[data-key="${key}"]`)?.selectedOptions[0]?.text ?? "").toLowerCase();
		const text = this.say("natural_weapon", {
			name: this.value(row, "name"),
			damage: bonus ? `${damage}${bonus > 0 ? "+" : ""}${bonus}` : damage,
			type: word("damage_type"),
			size: word("weapon_size"),
			throw: range ? this.say("natural_weapon_throw", { value: range }) : "",
		});
		return [text, true];
	}

	private drawPage(rows: HTMLElement[]) {
		this.pageTarget.replaceChildren(
			this.heading(this.say("panel_page")),
			...rows.map((row) => this.line(this.value(row, "description"), `• ${this.value(row, "name")}`)),
		);
	}
}
